import type { CSSProperties } from 'react';
import type { ChartThemePayload, ThemeAppearance } from '@lognai/plugin-analytics';
import { resolveDashboardTokens } from '@lognai/plugin-analytics/theme-runtime';

import { THEME_FONTS } from './theme-fonts';

export const readableText = (hex: string): string => {
  const rgb = [1, 3, 5]
    .map((i) => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map((n) => (n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4));
  const luminance = rgb[0]! * 0.2126 + rgb[1]! * 0.7152 + rgb[2]! * 0.0722;
  return luminance > 0.179 ? '#000000' : '#ffffff';
};
export const themeSurfaceStyle = (
  theme: ChartThemePayload,
  appearance: ThemeAppearance,
  presenting = false,
  inheritPageBackground = false
): CSSProperties => {
  const tokens = resolveDashboardTokens(theme, appearance, presenting);
  const semantic = theme[appearance].palette.semantic;
  const pageBackground = theme[appearance].presentation?.pageBackground && presenting
    ? theme[appearance].presentation.pageBackground
    : theme[appearance].dashboard?.pageBackground;
  const inheritsCanvas = inheritPageBackground && !pageBackground;
  return {
    ...(inheritsCanvas ? {} : { '--background': tokens.pageBackground }),
    '--surface': tokens.cardBackground,
    '--surface-foreground': tokens.text,
    '--surface-raised': tokens.cardBackground,
    '--foreground': tokens.text,
    '--muted-foreground': tokens.mutedText,
    '--border': tokens.border,
    '--border-strong': tokens.border,
    '--muted': `color-mix(in srgb, ${tokens.text} 8%, ${tokens.cardBackground})`,
    '--primary': tokens.accent,
    '--primary-foreground': readableText(tokens.accent),
    '--primary-subtle': `color-mix(in srgb, ${tokens.accent} 12%, ${tokens.cardBackground})`,
    '--ring': tokens.accent,
    '--success-default': semantic.positive,
    '--warning-default': semantic.warning,
    '--destructive-default': semantic.critical,
    '--theme-radius': `${tokens.cardRadius}px`,
    '--theme-shadow': tokens.cardStyle === 'elevated' ? '0 4px 18px rgba(0,0,0,0.12)' : 'none',
    '--theme-card-border': tokens.cardStyle === 'flat' ? 'transparent' : tokens.border,
    '--theme-padding':
      tokens.density === 'compact' ? '12px' : tokens.density === 'spacious' ? '24px' : '16px',
    '--theme-kpi-accent': tokens.kpiStyle === 'accent' ? tokens.accent : 'transparent',
    '--theme-kpi-background': tokens.kpiBackground ?? tokens.cardBackground,
    '--theme-header-background': tokens.headerBackground ?? tokens.cardBackground,
    '--theme-header-text': tokens.headerText ?? tokens.text,
    '--theme-header-muted': tokens.headerBackground === tokens.cardBackground ? tokens.mutedText : tokens.headerText ?? tokens.text,
    '--theme-header-muted-opacity': tokens.headerBackground === tokens.cardBackground ? '1' : '.8',
    '--theme-kpi-info': theme[appearance].palette.comparison.target,
    '--theme-heading-font': THEME_FONTS[theme[appearance].typography.headingFontStack ?? theme[appearance].typography.fontStack],
    color: tokens.text,
    backgroundColor: inheritsCanvas ? 'transparent' : tokens.pageBackground,
    fontFamily: THEME_FONTS[theme[appearance].typography.fontStack],
  } as CSSProperties;
};
